import React from "react";
import {
    Box,
    Typography,
    IconButton,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    FormControl,
    Select,
    MenuItem,
    useTheme,
    alpha,
    useMediaQuery
} from "@mui/material";
import {
    FirstPage,
    LastPage,
    KeyboardArrowLeft,
    KeyboardArrowRight
} from "@mui/icons-material";

import DeleteIcon from "../../assets/FinFactor/delete.svg";
import EditIcon from "../../assets/FinFactor/edit.svg";
import AppBoxComponent from "../mui/AppBoxComponent";
import AppButtonComponent from "../mui/AppButtonComponent";
import AppTypographyComponent from "../mui/AppTypographyComponent";

const CommonConsentTable = ({
    title = "",
    rows = [],
    columns = [],
    showActionColumn = true,
    onDelete = () => { },
    onEdit = null,
    variant = "default",
    pagination = false,
    pageSize = 10,
    currentPage = 1,
    totalRows = 0,
    onPageChange = () => { },
    onPageSizeChange = () => { },
    showPerRowDropdown = true,
    // Style Props
    containerSx = {},
    tableSx = {},
    headerSx = {},
    rowSx = {},
    cellSx = {},
    footerSx = {},
    paginationButtonSx = {},
    activePaginationButtonSx = {},
}) => {
    const theme = useTheme();
    const isAccountVariant = variant === "account";
    const isXs = useMediaQuery(theme.breakpoints.down('sm'));

    const totalPages = Math.ceil(totalRows / pageSize);
    const startRow = totalRows === 0 ? 0 : (currentPage - 1) * pageSize + 1;
    const endRow = Math.min(currentPage * pageSize, totalRows);

    return (
        <Box sx={{ mb: 4, ...containerSx }}>

            {title && (
                <Typography
                    sx={{
                        color: "white",
                        fontWeight: 600,
                        mb: 1.5,
                        fontSize: "14px"
                    }}
                >
                    {title}
                </Typography>
            )}

            <TableContainer
                component={Paper}
                sx={{
                    backgroundColor: "#2B3148", // Default Figma background
                    borderRadius: "10px",
                    overflowX: "auto",
                    border: "1px solid #3C4463", // Default Figma border
                    boxShadow: "none",
                    '&::-webkit-scrollbar': {
                        height: '6px',
                    },
                    '&::-webkit-scrollbar-thumb': {
                        backgroundColor: '#0d3aeeff',
                        borderRadius: '10px',
                    },
                    ...tableSx
                }}
            >
                <Table size="small">

                    {/* HEADER */}

                    <TableHead>
                        <TableRow
                            sx={{
                                backgroundColor: "#13172A", // Default Figma Header
                                ...headerSx
                            }}
                        >

                            {columns.map((col, idx) => (

                                <TableCell
                                    key={idx}
                                    align={col === "Sr No." ? "center" : "left"}
                                    sx={{
                                        color: "#A9B6E5", // Default Figma Text
                                        fontWeight: 600,
                                        fontSize: "13px",
                                        borderBottom: "1px solid #3C4463",
                                        borderRight:
                                            idx !== columns.length - 1 || showActionColumn
                                                ? "1px solid #3C4463"
                                                : "none",
                                        py: 2,
                                        px: 2.5,
                                        whiteSpace: "nowrap",
                                        ...cellSx
                                    }}
                                >
                                    {col}
                                </TableCell>

                            ))}

                            {showActionColumn && (

                                <TableCell
                                    align="center"
                                    sx={{
                                        color: "#A9B6E5",
                                        fontWeight: 600,
                                        fontSize: "13px",
                                        borderBottom: "1px solid #3C4463",
                                        width: 80,
                                        whiteSpace: "nowrap",
                                        ...cellSx
                                    }}
                                >
                                    Action
                                </TableCell>

                            )}

                        </TableRow>
                    </TableHead>


                    {/* BODY */}

                    <TableBody>

                        {rows.map((row, index) => (

                            <TableRow
                                key={index}
                                sx={{
                                    backgroundColor: "#2B3148",
                                    "&:hover": {
                                        backgroundColor: "#343B55"
                                    },
                                    ...rowSx
                                }}
                            >

                                {columns.map((colKey, colIdx) => (

                                    <TableCell
                                        key={colIdx}
                                        align={colKey === "Sr No." ? "center" : "left"}
                                        sx={{
                                            color: "#E6ECFF",
                                            fontSize: "13px",
                                            borderBottom: "1px solid #3C4463",
                                            borderRight:
                                                colIdx !== columns.length - 1 || showActionColumn
                                                    ? "1px solid #3C4463"
                                                    : "none",
                                            py: 2,
                                            px: 2.5,
                                            whiteSpace: "nowrap",
                                            ...cellSx
                                        }}
                                    >

                                        {row[colKey] ?? "-"}

                                    </TableCell>

                                ))}


                                {/* ACTION */}

                                {showActionColumn && (

                                    <TableCell
                                        align="center"
                                        sx={{
                                            borderBottom: "1px solid #3C4463",
                                            whiteSpace: "nowrap",
                                            ...cellSx
                                        }}
                                    >

                                        <Box
                                            sx={{
                                                display: "flex",
                                                justifyContent: "center",
                                                gap: 1.5
                                            }}
                                        >

                                            {onEdit && (

                                                <IconButton
                                                    size="small"
                                                    onClick={() => onEdit(row, index)}
                                                    sx={{ p: 0.5 }}
                                                >

                                                    <img src={EditIcon} width={18} />

                                                </IconButton>

                                            )}

                                            <IconButton
                                                size="small"
                                                onClick={() => onDelete(row, index)}
                                                sx={{ p: 0.5 }}
                                            >

                                                <img src={DeleteIcon} width={18} />

                                            </IconButton>

                                        </Box>

                                    </TableCell>

                                )}

                            </TableRow>

                        ))}

                    </TableBody>

                </Table>

            </TableContainer>

            {/* Custom Pagination */}
            {pagination && rows.length > 0 && (
                <AppBoxComponent
                    sx={{
                        display: "grid",
                        gridTemplateColumns: { xs: "1fr", sm: "1fr auto 1fr" },
                        alignItems: "center",
                        width: "100%",
                        p: theme.spacing(2),
                        borderTop: `1px solid ${alpha(theme.palette.common.white, 0.08)}`,
                        gap: { xs: theme.spacing(1), sm: 0 },
                        mt: 2,
                        backgroundColor: "#13172A",
                        borderRadius: "0 0 10px 10px",
                        border: "1px solid #3C4463",
                        ...footerSx
                    }}
                >
                    {/* Left side - Row count */}
                    <AppTypographyComponent sx={{
                        color: theme.palette.grey[400],
                        fontSize: "13px",
                        textAlign: { xs: 'center', sm: 'left' }
                    }}>
                        {startRow}-{endRow} of {totalRows} rows
                    </AppTypographyComponent>

                    {/* Center - Navigation buttons */}
                    <AppBoxComponent
                        sx={{
                            gridColumn: { xs: "1", sm: "2" },
                            display: "flex",
                            gap: { xs: theme.spacing(0.5), sm: theme.spacing(1) },
                            justifyContent: 'center',
                            alignItems: 'center'
                        }}
                    >
                        <AppButtonComponent
                            size="small"
                            onClick={() => onPageChange(1)}
                            disabled={currentPage === 1}
                            sx={{
                                color: theme.palette.grey[400],
                                minWidth: { xs: theme.spacing(4), sm: theme.spacing(4.5) },
                                height: { xs: theme.spacing(4), sm: theme.spacing(4.5) },
                                borderRadius: theme.spacing(0.5),
                                padding: 0,
                                backgroundColor: alpha(theme.palette.common.white, 0.02),
                                border: `1px solid ${alpha(theme.palette.common.white, 0.08)}`,
                                '&:disabled': {
                                    color: theme.palette.grey[600],
                                    backgroundColor: 'transparent',
                                    border: `1px solid ${alpha(theme.palette.common.white, 0.04)}`,
                                },
                                '&:hover': {
                                    backgroundColor: alpha(theme.palette.common.white, 0.05),
                                },
                                ...paginationButtonSx
                            }}
                        >
                            <FirstPage sx={{ fontSize: 20 }} />
                        </AppButtonComponent>
                        <AppButtonComponent
                            size="small"
                            onClick={() => onPageChange(currentPage - 1)}
                            disabled={currentPage === 1}
                            sx={{
                                color: theme.palette.grey[400],
                                minWidth: { xs: theme.spacing(4), sm: theme.spacing(4.5) },
                                height: { xs: theme.spacing(4), sm: theme.spacing(4.5) },
                                borderRadius: theme.spacing(0.5),
                                padding: 0,
                                backgroundColor: alpha(theme.palette.common.white, 0.02),
                                border: `1px solid ${alpha(theme.palette.common.white, 0.08)}`,
                                '&:disabled': {
                                    color: theme.palette.grey[600],
                                    backgroundColor: 'transparent',
                                    border: `1px solid ${alpha(theme.palette.common.white, 0.04)}`,
                                },
                                '&:hover': {
                                    backgroundColor: alpha(theme.palette.common.white, 0.05),
                                },
                                ...paginationButtonSx
                            }}
                        >
                            <KeyboardArrowLeft sx={{ fontSize: 20 }} />
                        </AppButtonComponent>
                        {/* Page number buttons */}
                        {[...Array(Math.min(isXs ? 3 : 5, totalPages))].map((_, idx) => {
                            let pageNum;
                            const maxPages = isXs ? 3 : 5;
                            if (totalPages <= maxPages) {
                                pageNum = idx + 1;
                            } else if (currentPage <= Math.ceil(maxPages / 2)) {
                                pageNum = idx + 1;
                            } else if (currentPage >= totalPages - Math.floor(maxPages / 2)) {
                                pageNum = totalPages - maxPages + idx + 1;
                            } else {
                                pageNum = currentPage - Math.floor(maxPages / 2) + idx;
                            }

                            if (pageNum > totalPages || pageNum < 1) return null;

                            return (
                                <AppButtonComponent
                                    key={pageNum}
                                    size="small"
                                    onClick={() => onPageChange(pageNum)}
                                    sx={{
                                        backgroundColor: currentPage === pageNum ? theme.palette.primary.main : alpha(theme.palette.common.white, 0.02),
                                        color: theme.palette.common.white,
                                        minWidth: { xs: theme.spacing(4), sm: theme.spacing(4.5) },
                                        height: { xs: theme.spacing(4), sm: theme.spacing(4.5) },
                                        padding: 0,
                                        borderRadius: theme.spacing(0.5),
                                        fontSize: "14px",
                                        border: currentPage === pageNum ? 'none' : `1px solid ${alpha(theme.palette.common.white, 0.08)}`,
                                        "&:hover": {
                                            backgroundColor: currentPage === pageNum ? theme.palette.primary.dark : alpha(theme.palette.common.white, 0.05),
                                        },
                                        ...(currentPage === pageNum ? activePaginationButtonSx : paginationButtonSx)
                                    }}
                                >
                                    {pageNum}
                                </AppButtonComponent>
                            );
                        })}
                        <AppButtonComponent
                            size="small"
                            onClick={() => onPageChange(currentPage + 1)}
                            disabled={currentPage === totalPages}
                            sx={{
                                color: theme.palette.grey[400],
                                minWidth: { xs: theme.spacing(4), sm: theme.spacing(4.5) },
                                height: { xs: theme.spacing(4), sm: theme.spacing(4.5) },
                                borderRadius: theme.spacing(0.5),
                                padding: 0,
                                backgroundColor: alpha(theme.palette.common.white, 0.02),
                                border: `1px solid ${alpha(theme.palette.common.white, 0.08)}`,
                                '&:disabled': {
                                    color: theme.palette.grey[600],
                                    backgroundColor: 'transparent',
                                    border: `1px solid ${alpha(theme.palette.common.white, 0.04)}`,
                                },
                                '&:hover': {
                                    backgroundColor: alpha(theme.palette.common.white, 0.05),
                                },
                                ...paginationButtonSx
                            }}
                        >
                            <KeyboardArrowRight sx={{ fontSize: 20 }} />
                        </AppButtonComponent>
                        <AppButtonComponent
                            size="small"
                            onClick={() => onPageChange(totalPages)}
                            disabled={currentPage === totalPages}
                            sx={{
                                color: theme.palette.grey[400],
                                minWidth: { xs: theme.spacing(4), sm: theme.spacing(4.5) },
                                height: { xs: theme.spacing(4), sm: theme.spacing(4.5) },
                                borderRadius: theme.spacing(0.5),
                                padding: 0,
                                backgroundColor: alpha(theme.palette.common.white, 0.02),
                                border: `1px solid ${alpha(theme.palette.common.white, 0.08)}`,
                                '&:disabled': {
                                    color: theme.palette.grey[600],
                                    backgroundColor: 'transparent',
                                    border: `1px solid ${alpha(theme.palette.common.white, 0.04)}`,
                                },
                                '&:hover': {
                                    backgroundColor: alpha(theme.palette.common.white, 0.05),
                                },
                                ...paginationButtonSx
                            }}
                        >
                            <LastPage sx={{ fontSize: 20 }} />
                        </AppButtonComponent>
                    </AppBoxComponent>

                    {/* Right side - Rows per page dropdown */}
                    <AppBoxComponent
                        sx={{
                            gridColumn: { xs: "1", sm: "3" },
                            display: "flex",
                            justifyContent: { xs: 'center', sm: 'flex-end' }
                        }}
                    >
                        {showPerRowDropdown && (
                            <FormControl size="small" sx={{
                                minWidth: theme.spacing(15),
                                width: 'auto',
                            }}>
                                <Select
                                    value={pageSize}
                                    onChange={(e) => onPageSizeChange(e.target.value)}
                                    sx={{
                                        color: "white",
                                        fontSize: "13px",
                                        backgroundColor: "#13172A",
                                        border: `1px solid ${alpha(theme.palette.common.white, 0.08)}`,
                                        borderRadius: theme.spacing(0.5),
                                        '& .MuiOutlinedInput-notchedOutline': {
                                            borderColor: alpha(theme.palette.common.white, 0.08),
                                        },
                                        '&:hover .MuiOutlinedInput-notchedOutline': {
                                            borderColor: alpha(theme.palette.common.white, 0.2),
                                        },
                                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                            borderColor: "#55B4E3",
                                            borderWidth: "1.5px"
                                        },
                                        '& .MuiSvgIcon-root': {
                                            color: theme.palette.grey[400],
                                        },
                                        '& .MuiSelect-select': {
                                            py: 1,
                                            px: 2,
                                        }
                                    }}
                                    MenuProps={{
                                        PaperProps: {
                                            sx: {
                                                backgroundColor: "#13172A",
                                                border: "1px solid rgba(255, 255, 255, 0.1)",
                                                mt: 0.5,
                                                '& .MuiMenuItem-root': {
                                                    color: "white",
                                                    fontSize: "13px",
                                                    '&:hover': {
                                                        backgroundColor: "rgba(255, 255, 255, 0.05)",
                                                    },
                                                    '&.Mui-selected': {
                                                        backgroundColor: "rgba(255, 255, 255, 0.1)",
                                                        color: "#55B4E3",
                                                        "&:hover": {
                                                            backgroundColor: "rgba(255, 255, 255, 0.15)",
                                                        }
                                                    },
                                                },
                                            },
                                        },
                                    }}
                                >
                                    {[10, 20, 50, 100].map((size) => (
                                        <MenuItem key={size} value={size}>
                                            {size} per page
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        )}
                    </AppBoxComponent>
                </AppBoxComponent>
            )}

        </Box>
    );
};

export default CommonConsentTable;

